import { useRef, useState, useEffect } from "react";

const DIRECTION_LOCK_THRESHOLD = 30;
const SWIPE_THRESHOLD = 80;

export default function useSwipeGesture({ onSwipeLeft, onSwipeRight, enabled = true }) {
  const ref = useRef(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const touchState = useRef({
    startX: 0,
    startY: 0,
    locked: null,
    currentOffset: 0,
  });

  const onSwipeLeftRef = useRef(onSwipeLeft);
  const onSwipeRightRef = useRef(onSwipeRight);
  onSwipeLeftRef.current = onSwipeLeft;
  onSwipeRightRef.current = onSwipeRight;

  useEffect(() => {
    if (!enabled || !ref.current) return;
    const el = ref.current;

    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      touchState.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        locked: null,
        currentOffset: 0,
      };
    };

    const handleTouchMove = (e) => {
      const touch = e.touches[0];
      const dx = touch.clientX - touchState.current.startX;
      const dy = touch.clientY - touchState.current.startY;

      if (!touchState.current.locked) {
        if (Math.abs(dx) < DIRECTION_LOCK_THRESHOLD
            && Math.abs(dy) < DIRECTION_LOCK_THRESHOLD) {
          return;
        }
        touchState.current.locked =
          Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
      }

      if (touchState.current.locked === "horizontal") {
        e.preventDefault();
        touchState.current.currentOffset = dx;
        setSwipeOffset(dx);
        setIsSwiping(true);
      }
    };

    const handleTouchEnd = () => {
      const offset = touchState.current.currentOffset;
      if (touchState.current.locked === "horizontal") {
        if (offset < -SWIPE_THRESHOLD && onSwipeLeftRef.current) {
          onSwipeLeftRef.current();
        } else if (offset > SWIPE_THRESHOLD && onSwipeRightRef.current) {
          onSwipeRightRef.current();
        }
      }
      touchState.current.currentOffset = 0;
      touchState.current.locked = null;
      setIsSwiping(false);
      requestAnimationFrame(() => {
        setSwipeOffset(0);
      });
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });
    el.addEventListener("touchcancel", handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [enabled]);

  if (!enabled) {
    return { ref, swipeOffset: 0, isSwiping: false };
  }
  return { ref, swipeOffset, isSwiping };
}
